import React from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ro';
import { useTranslation } from '../../hooks/useTranslation';
import ExpandableContent from '../ui/ExpandableContent';
import StarRating from '../ui/StarRating';

// Approved buyer reviews from the api's product payload (`product.reviews`:
// average, count, the newest items). Nothing renders until there is one.
export default function ProductReviews({ reviews }) {
  const { t, locale } = useTranslation('product');
  if (!reviews?.count) return null;
  const average = Number(reviews.average).toLocaleString(locale === 'en' ? 'en-GB' : 'ro-RO', { maximumFractionDigits: 1 });
  const stars = (rating, size) => <div role="img" aria-label={t('reviews.stars').replace('{rating}', rating)}>
    <StarRating rating={Math.round(rating)} readonly size={size} />
  </div>;

  return <section id="product-reviews" aria-labelledby="product-reviews-title" className="mt-12 min-w-0 scroll-mt-24 pt-4">
    <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2">
      <h2 id="product-reviews-title" className="text-2xl font-black tracking-tight">{t('reviews.title')}<span className="text-teal-700">.</span></h2>
      <div className="flex items-center gap-2 text-sm">
        {stars(reviews.average, 'small')}
        <span className="font-black">{average}</span>
        <span className="text-krit-muted">· {(reviews.count === 1 ? t('reviews.one') : t('reviews.many')).replace('{count}', reviews.count)}</span>
      </div>
    </div>
    <ul className="grid gap-4 sm:grid-cols-2">
      {reviews.items.map((review) => <li key={review.id} className="min-w-0 rounded-2xl bg-krit-paper px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {stars(review.rating, 'small')}
          <time dateTime={review.createdAt} className="text-xs text-krit-muted">{dayjs(review.createdAt).locale(locale).format('D MMM YYYY')}</time>
        </div>
        {review.comment && <div className="mt-3">
          <ExpandableContent paper collapsedHeight={120} moreLabel={t('info.more')} lessLabel={t('info.less')}>
            <p className="whitespace-pre-line break-words text-sm leading-6 text-krit-surface">{review.comment}</p>
          </ExpandableContent>
        </div>}
        <p className="mt-3 text-xs font-bold text-krit-ink">{review.authorName} <span className="font-semibold text-teal-700">· {t('reviews.verified')}</span></p>
      </li>)}
    </ul>
  </section>;
}
